import * as Y from "yjs";
import * as awarenessProtocol from "y-protocols/awareness";
import { io, Socket } from "socket.io-client";
import {
  joinCollabSession,
  leaveCollabSession,
  syncCollabState,
} from "controller/api/kb/kb";
import type {
  SocketCollabProviderConfig,
  SocketCollabProviderInstance,
} from "shared/types/kb-collaboration";
import { generateUserColor } from "./utils";
import { debugConsole } from "utilz/helpers/debugConsole";

/**
 * Creates a Socket.IO-based collaboration provider for Yjs
 * Handles real-time sync, awareness, and offline queue
 * Supports multi-pod backend via Socket.IO rooms
 */

export const createSocketCollabProvider = (
  ydoc: Y.Doc,
  awareness: awarenessProtocol.Awareness,
  config: SocketCollabProviderConfig,
): SocketCollabProviderInstance => {
  let socket: Socket;
  const eventHandlers = new Map<string, Set<Function>>();
  const offlineQueue: any[] = [];
  const processedUpdates = new Set<string>();
  const processedPresenceJoins = new Set<string>();
  const MAX_PROCESSED_CACHE = 1000;

  // Room name based on snapshot ID
  const roomName = `kb:${config.snapshotId}`;

  // Socket.IO event names (replacing MQTT topics)
  const events = {
    publishUpdate: "collab:publish-update",
    update: "collab:update",
    awareness: "collab:awareness",
    presence: "collab:presence",
    join: "collab:join",
    joinAck: "collab:join-ack",
    broadcast: "collab:broadcast",
    publishTiptap: "collab:publish-tiptap-update",
    publishGeneratedYjs: "collab:publish-generated-yjs",
  };

  // Event emitter
  const emit = (event: string, data: any) => {
    const handlers = eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  };

  // Publish update to backend
  const publishUpdate = (packet: any) => {
    try {
      if (!socket.connected) {
        // Queue for later when offline
        offlineQueue.push(packet);
        emit("status", {
          status: "offline",
          queuedMessages: offlineQueue.length,
        });
        return;
      }
      // Emit with room and metadata for backend routing
      socket.emit(events.publishUpdate, {
        room: roomName,
        mspId: config.mspId,
        snapshotId: config.snapshotId,
        userId: config.userId,
        clientId: ydoc.clientID,
        packet: packet,
        timestamp: Date.now(),
      });
    } catch (error) {
      debugConsole("Failed to publish update:", error);
    }
  };

  const publishTipTapUpdate = (packet: any) => {
    try {
      debugConsole("publishing tip tap update", packet);
      if (!socket.connected) {
        // Queue for later when offline
        offlineQueue.push(packet);
        emit("status", {
          status: "offline",
          queuedMessages: offlineQueue.length,
        });
        return;
      }

      // Emit with room and metadata for backend routing
      socket.emit(events.publishTiptap, {
        room: roomName,
        mspId: config.mspId,
        snapshotId: config.snapshotId,
        articleId: config.articleId,
        userId: config.userId,
        clientId: ydoc.clientID,
        content: packet.content,
        fileName: packet.filename,
        timestamp: Date.now(),
      });
    } catch (error) {
      debugConsole("Failed to publish update:", error);
    }
  };

  const publishGeneratedYjs = (yjsUpdate: Uint8Array) => {
    try {
      if (!socket.connected) {
        debugConsole(
          "[publishGeneratedYjs] Socket not connected, cannot save generated Yjs",
        );
        return;
      }

      socket.emit(events.publishGeneratedYjs, {
        articleId: config.articleId,
        yjs_update: Array.from(yjsUpdate),
        room: roomName,
        mspId: config.mspId,
        snapshotId: config.snapshotId,
        userId: config.userId,
        timestamp: Date.now(),
      });

      debugConsole(
        "[publishGeneratedYjs] Emitted generated Yjs update to backend",
      );
    } catch (error) {
      debugConsole("[publishGeneratedYjs] Failed to publish:", error);
    }
  };

  // Join collaboration session
  const joinSession = async () => {
    try {
      // Clear processedUpdates cache to prevent duplicate detection of historical updates
      processedUpdates.clear();

      const stateVector = Y.encodeStateVector(ydoc);
      const response = await joinCollabSession({
        mappingId: config.snapshotId,
        kbRecordId: config.kbRecordId,
        userId: config.userId,
        mspId: config.mspId,
        stateVector: Array.from(stateVector),
      });
      awareness.setLocalState({
        user: {
          id: config.userId,
          name: config.userName,
          color: config.userColor,
          avatar: config.userAvatar,
        },
      });

      awareness.setLocalStateField("user", {
        id: config.userId,
        name: config.userName,
        color: config.userColor,
        avatar: config.userAvatar,
      });
      emit("joinSuccess", response);

      // Emit join success
    } catch (error) {
      debugConsole("Failed to join collaboration session:", error);
      emit("error", error);
    }
  };

  // Debounced awareness publishing (100ms debounce)
  let awarenessDebounceTimer: NodeJS.Timeout | null = null;
  let lastAwarenessUpdate: Uint8Array | null = null;
  let awarenessRetryTimer: NodeJS.Timeout | null = null;

  const debouncedPublishAwareness = (update: Uint8Array) => {
    if (awarenessDebounceTimer) clearTimeout(awarenessDebounceTimer);
    lastAwarenessUpdate = update;

    awarenessDebounceTimer = setTimeout(() => {
      // Check if Socket.IO is connected before publishing
      if (!socket || !socket.connected) {
        // Retry after 2 seconds
        if (awarenessRetryTimer) clearTimeout(awarenessRetryTimer);
        awarenessRetryTimer = setTimeout(() => {
          if (lastAwarenessUpdate) {
            debouncedPublishAwareness(lastAwarenessUpdate);
          }
        }, 2000);
        return;
      }

      // Use volatile for fire-and-forget (like MQTT QoS 0)
      socket.emit(events.awareness, {
        room: roomName,
        clientId: ydoc.clientID,
        update: Array.from(update),
        timestamp: Date.now(),
      });
      lastAwarenessUpdate = null; // Clear after successful publish
    }, 100);
  };

  // Handle incoming Socket.IO messages
  const handleUpdateMessage = (data: any) => {
    try {
      // CRITICAL: Ignore own updates to prevent echo
      if (data.clientId === ydoc.clientID) {
        return;
      }

      if (data.snapshotId && data.snapshotId !== config.snapshotId) {
        return;
      }

      // Deduplication check
      const updateHash = `${data.clientId}-${data.timestamp}`;
      if (processedUpdates.has(updateHash)) {
        return;
      }

      processedUpdates.add(updateHash);
      if (processedUpdates.size > MAX_PROCESSED_CACHE) {
        const firstKey = processedUpdates.values().next().value;
        processedUpdates.delete(firstKey || "");
      }

      const updateArray = new Uint8Array(data.update);
      try {
        try {
          // Apply update with "remote" origin to prevent echo/re-publishing
          Y.applyUpdate(ydoc, updateArray, "remote");
        } catch (err) {
          debugConsole(err);
          return;
        }

      } catch (error) {
        debugConsole("Failed to apply remote update:", error);
      }
    } catch (error) {
      debugConsole("Failed to handle Socket.IO message:", error);
    }
  };

  const handleAwarenessMessage = (data: any) => {
    try {
      if (data.clientId === ydoc.clientID) return;
      // Apply remote awareness update
      awarenessProtocol.applyAwarenessUpdate(
        awareness,
        new Uint8Array(data.update),
        "remote",
      );
    } catch (error) {
      debugConsole("Failed to handle awareness message:", error);
    }
  };

  // Extract document update logic to a separate function
  const applyDocumentUpdate = (data: any, source: string) => {
    debugConsole(`[${source}] Processing document update:`, {
      hasUpdate: !!data.update,
      updateLength: data.update?.length || 0,
      userId: data.userId,
      timestamp: data.timestamp,
    });

    // Validate that update exists and has content
    if (!data.update || data.update.length === 0) {
      debugConsole(`[${source}] Received empty update from backend, ignoring`);
      return false;
    }

    // Prevent duplicate processing of join events
    const joinHash = `${data.userId}-${data.timestamp}`;
    if (processedPresenceJoins.has(joinHash)) {
      debugConsole(`[${source}] Duplicate join event detected, skipping`);
      return false;
    }
    processedPresenceJoins.add(joinHash);

    // debugConsole(`[${source}] Before applying update:`, {
    //   clientID: ydoc.clientID,
    //   fragmentLength: ydoc.getXmlFragment("default").length,
    //   updateSize: data.update.length,
    //   joiningUserId: data.userId,
    // });

    try {
      const updateArray = new Uint8Array(data.update);

      // Additional validation: Check if update is a valid Yjs update
      if (updateArray.length === 0) {
        debugConsole(`[${source}] Update array is empty, skipping`);
        return false;
      }

      // Apply update with "remote" origin to prevent echo/re-publishing
      Y.applyUpdate(ydoc, updateArray, "remote");

      const fragment = ydoc.getXmlFragment("default");
      // debugConsole(`[${source}] After applying update:`);
      // debugConsole("  - Fragment JSON:", fragment.toJSON());
      // debugConsole("  - Fragment length:", fragment.length);
      // debugConsole("  - Fragment toString:", fragment.toString());

      emit("joinSuccess", {
        snapshotId: config.snapshotId,
        userId: config.userId,
      });
      return true;
    } catch (error) {
      debugConsole(`[${source}] Error applying update:`, error);
      return false;
    }
  };

  const handlePresenceMessage = (data: any) => {
    try {
      debugConsole("[Presence] Message received:", data);

      if (data.type === "user_joined") {
        applyDocumentUpdate(data, "Presence Join");
      }

      emit("presence", data);
    } catch (error) {
      debugConsole("Failed to handle presence message:", error);
    }
  };

  const handleJoinAck = (data: any) => {
    try {
      emit("joinAck", data);
    } catch (error) {
      debugConsole("Failed to handle join ack:", error);
    }
  };

  const handleBroadcast = (data: any) => {
    try {
      emit("broadcast", data);
    } catch (error) {
      debugConsole("Failed to handle broadcast:", error);
    }
  };

  // Flush offline queue on reconnect
  const flushOfflineQueue = () => {
    while (offlineQueue.length > 0) {
      const packet = offlineQueue.shift();
      publishUpdate(packet);
    }
  };

  // Handle reconnection
  const handleReconnect = async () => {
    try {
      // Flush offline queue first
      flushOfflineQueue();
      // Manual sync to get missing updates

      const stateVector = Y.encodeStateVector(ydoc);
      const base64StateVector = btoa(String.fromCharCode(...stateVector));
      const arr = Array.from(stateVector);

      const b64 = Buffer.from(arr).toString("base64");
      const response = await syncCollabState({
        mappingId: config.snapshotId,
        userId: config.userId,
        mspId: config.mspId,
        // @ts-ignore
        stateVector: b64,
      });
    } catch (error) {
      debugConsole("Failed to sync on reconnect:", error);
    }
  };

  // Setup event handlers
  const setupEventHandlers = () => {
    // Awareness update handler - publish (debounced)
    awareness.on("update", ({ added, updated, removed }: any) => {
      const update = awarenessProtocol.encodeAwarenessUpdate(awareness, [
        ...added,
        ...updated,
        ...removed,
      ]);
      debouncedPublishAwareness(update);
    });

    // Socket.IO event handlers
    socket.on(events.update, handleUpdateMessage);
    socket.on(events.awareness, handleAwarenessMessage);
    socket.on(events.presence, handlePresenceMessage);
    socket.on(events.joinAck, handleJoinAck);
    socket.on(events.broadcast, handleBroadcast);

    socket.on("connect", () => {
      debugConsole("Socket.IO connected");
      emit("status", { status: "connected" });
      joinSession();
    });

    socket.on("error", (err: Error) => {
      debugConsole("Socket.IO error:", err);
      emit("error", err);
      emit("status", { status: "error" });
    });

    socket.on("reconnect", () => {
      debugConsole("Socket.IO reconnecting...");
      handleReconnect();
    });

    socket.on("disconnect", () => {
      debugConsole("Socket.IO disconnected");
      awareness.setLocalState(null);

      emit("status", {
        status: "offline",
        queuedMessages: offlineQueue.length,
      });
    });
  };

  // Initialize Socket.IO client
  const setupSocketClient = async () => {
    // debugConsole(
    //   "[setupSocketClient] Creating Socket.IO client for ydoc clientID:",
    //   ydoc.clientID
    // );

    socket = io(`${import.meta.env.VITE_APP_SOCKET_URL}`, {
      auth: {
        // Add token from auth system if needed
        // token: getAuthToken(),
      },
      query: {
        userId: config.userId,
        mspId: config.mspId,
        snapshotId: config.snapshotId,
        userName: config.userName,
        userColor: config.userColor,
        userAvatar: config.userAvatar,
      },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    setupEventHandlers();
  };

  // Cleanup and leave session
  const destroy = async () => {
    try {
      awareness.setLocalState(null);

      await leaveCollabSession({
        mappingId: config.snapshotId,
        userId: config.userId,
        mspId: config.mspId,
      });
    } catch (error) {
      debugConsole("Failed to leave session:", error);
    }

    if (awarenessDebounceTimer) {
      clearTimeout(awarenessDebounceTimer);
    }
    if (awarenessRetryTimer) {
      clearTimeout(awarenessRetryTimer);
    }

    socket.disconnect();
  };

  // Event listener management
  const on = (event: string, handler: Function) => {
    if (!eventHandlers.has(event)) {
      eventHandlers.set(event, new Set());
    }
    eventHandlers.get(event)!.add(handler);
  };

  const off = (event: string, handler: Function) => {
    eventHandlers.get(event)?.delete(handler);
  };

  // Initialize
  setupSocketClient();

  // Return provider instance
  return {
    destroy,
    get isConnected() {
      return socket?.connected || false;
    },
    on,
    off,
    publishUpdate,
    publishTipTapUpdate,
    publishGeneratedYjs,
  };
};
